<template>
  <div
    class="card h-100 border border-border/10 shadow-card bg-white round-xl hover:shadow-md"
  >
    <div class="card-body">
      <div class="d-flex flex-column align-middle">
        <!-- reviewiewer Info -->
        <div class="d-flex align-items-center gap-y-3">
          <div
            class="round-circle bg-primary d-flex align-items-center justify-content-center"
            style="width: 40px; height: 40px"
          >
            <img
              v-if="r.author_image"
              :src="r.author_image"
              alt="Author"
              class="rounded-circle"
              style="width: 40px; height: 40px"
            />
            <span v-else class="small text-white">
              {{ getInitials(r.author_title) }}
            </span>
          </div>
          <div class="flex-grow-1 min-w-0">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h3 class="font-semibold text-gray mb-0">{{
                  r.author_title
                }}</h3>
                <div class="d-flex align-items-center gap-y-3 mt-1">
                  <div class="d-flex align-items-center gap-y-1">
                    <span
                      v-for="s in r.review_rating"
                      :key="s"
                      class="text-warning"
                      style="font-size: 16px"
                      >&#9733;</span
                    >
                  </div>
                  <span class="text-sm text-gray">{{ r.days_ago }}</span>
                </div>
              </div>
              <div class="d-flex align-items-center gap-y-2">
                <!-- Posted to Google/GBP: reply_updated is set by sync or after successful post -->
                <span
                  v-if="isReplyPostedToGoogle"
                  class="badge bg-success text-success round-xl bg-opacity-20"
                >
                  <i class="uil uil-check me-1" style="font-size: 10px"></i>
                  Replied
                </span>
                <span
                  v-else-if="hasReplyDraft"
                  class="badge bg-warning text-warning round-xl bg-opacity-20"
                >
                  Pending reply
                </span>
                <span
                  v-else
                  class="badge bg-warning text-warning round-xl bg-opacity-20"
                >
                  Needs reply
                </span>
                <span
                  class="badge bg-primary text-primary round-xl bg-opacity-20"
                >
                  {{ r.platform || 'Google' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- reviewiew Text -->
        <p class="mt-3 text-sm text-gray line-clamp-4">{{
          r.review_text
        }}</p>

        <!-- Reply Section (only show if replyText exists or draft) -->
        <div
          v-if="r.replyText || r.reply || r.writing_reply"
          class="mt-3 pl-3"
          style="border-left: 1px solid #c6c6c7 !important"
        >
          <div class="bg-gray round-xl border-border/20 p-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <div class="d-flex align-items-center gap-y-2">
                <i class="uil uil-comment-alt text-gray"></i>
                <span class="text-xs font-medium text-gray font-weight-bold">
                  Your Reply:
                </span>
              </div>
              <div class="d-flex align-items-center gap-y-2">
                <span class="text-xs text-success" v-if="r.repliedAt">
                  Updated: {{ r.repliedAt }}
                </span>
                <button
                  v-if="
                    user.business.is_reply_engine &&
                    (user.business.owner.has_re ||
                      user.business.owner.has_repu_agency ||
                      user.business.owner?.agency?.free_fee) &&
                    !r.waiting_4_ai &&
                    r.stopped_typing
                  "
                  class="btn btn-sm px-2 py-1 text-xs text-gray"
                  @click="startInlineEdit(review)"
                >Edit</button>
              </div>
            </div>
            <!-- Inline editable reply -->
            <div v-if="review.editing">
              <textarea
                v-model="review.reply"
                rows="4"
                class="form-control text-xs"
              ></textarea>
            </div>
            <p v-else class="text-xs text-gray leading-relaxed mb-0">
              {{ r.replyText }}
            </p>
          </div>
        </div>

        <!-- Reply Buttons (only when actions are provided, e.g. on Reviews page) -->
        <div v-if="hasActions" class="mt-4 pt-4 border-top space-y-3">
          <div class="space-y-2">
            <div class="d-flex align-items-start gap-y-3">
              <span class="text-xs font-medium text-gray min-w-fit"
                >Reply:</span
              >
              <div class="d-flex align-items-start gap-y-3">
                <!-- Reply action -->
                <div class="d-flex flex-column align-items-center">
                  <button
                    v-if="!r.waiting_4_ai"
                    :disabled="r.working"
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    @click="createReply && createReply(review)"
                  >
                    <i class="uil uil-message"></i>
                    <span class="ms-1">
                      {{ isReplyPostedToGoogle ? 'Reply Posted' : 'Write Reply' }}
                    </span>
                    <b-spinner v-if="r.working" small />
                    <span v-if="isReplyPostedToGoogle" class="text-xs text-gray ms-2">&#10003;</span>
                  </button>
                  <small
                    v-if="isReplyPostedToGoogle"
                    class="text-xs text-success mt-1"
                  >Updated: {{ r.repliedAt }}</small>
                </div>

                <!-- Re-write (icon uil-restart like origin) -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    v-if="!r.waiting_4_ai && hasReplyDraft"
                    :disabled="r.working"
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    @click="rewriteReply && rewriteReply(review)"
                  >
                    <i class="uil uil-restart"></i>
                    <span class="ms-1">Re-write</span>
                    <b-spinner v-if="r.working" small />
                  </button>
                  <small
                    v-if="r.shared && r.shared.instagram"
                    class="text-xs text-muted mt-1"
                  >Instagram: {{ r.shared.instagram }}</small>
                </div>

                <!-- Post to Google -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    v-if="!r.waiting_4_ai"
                    :disabled="r.working"
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    @click="saveReply && saveReply(review)"
                  >
                    <i class="uil uil-upload"></i>
                    <span class="ms-1">Post</span>
                    <b-spinner v-if="r.working" small />
                  </button>
                  <small
                    v-if="r.shared && r.shared.google"
                    class="text-xs text-muted mt-1"
                  >Google: {{ r.shared.google }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Share Buttons (only when actions are provided) -->
        <div v-if="hasActions" class="mt-4 pt-4 border-top space-y-3">
          <div class="space-y-2">
            <div class="d-flex align-items-start gap-y-3">
              <span class="text-xs font-medium text-gray min-w-fit"
                >Share:</span
              >
              <div class="d-flex align-items-start gap-y-3">
                <!-- Facebook -->
                <div class="d-flex flex-column align-items-center">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    :disabled="!!r.facebook_post"
                    @click="confirmPostSocial && confirmPostSocial(review, 'facebook')"
                  >
                    <i class="uil uil-facebook-f"></i>
                    <span class="ms-1">Facebook</span>
                    <span
                      v-if="r.facebook_post"
                      class="text-xs text-gray ms-2"
                      >&#10003;</span
                    >
                  </button>
                  <small
                    v-if="r.facebook_post"
                    class="text-xs text-success mt-1"
                    >Facebook:
                    {{ r.facebook_post && r.facebook_post.front_date ? r.facebook_post.front_date.substr(0, 8) : '' }}</small
                  >
                </div>

                <!-- Instagram -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    :disabled="!!r.instagram_post"
                    @click="confirmPostSocial && confirmPostSocial(review, 'instagram')"
                  >
                    <i class="uil uil-instagram"></i>
                    <span class="ms-1">Instagram</span>
                    <span
                      v-if="r.instagram_post"
                      class="text-xs text-gray ms-2"
                      >&#10003;</span
                    >
                  </button>
                  <small
                    v-if="r.instagram_post"
                    class="text-xs text-success mt-1"
                    >Instagram:
                    {{ (r.instagram_post && r.instagram_post.front_date) ? r.instagram_post.front_date.substr(0, 8) : (r.shared && r.shared.instagram) || '' }}</small
                  >
                </div>

                <!-- Google -->
                <div class="d-flex flex-column align-items-start">
                  <button
                    class="btn btn-sm text-gray round-xl border h-7 text-xs px-3 d-flex align-items-center gap-y-2"
                    :disabled="!!r.gbp_post"
                    @click="confirmPostSocial && confirmPostSocial(review, 'gbp')"
                  >
                    <i class="uil uil-google"></i>
                    <span class="ms-1">Google</span>
                    <span
                      v-if="r.gbp_post"
                      class="text-xs text-success ms-2"
                      >&#10003;</span
                    >
                  </button>
                  <small
                    v-if="r.gbp_post"
                    class="text-xs text-muted mt-1"
                  >Google: {{ (r.gbp_post && typeof r.gbp_post === 'object' && r.gbp_post.front_date) ? r.gbp_post.front_date : (r.shared && r.shared.google) || r.gbp_post }}</small
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ReviewCard',
  props: {
    review: {
      type: Object,
      required: true,
    },
  },
  inject: {
    createReply: { default: null },
    rewriteReply: { default: null },
    saveReply: { default: null },
    confirmPostSocial: { default: null },
  },
  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    // Normalize review so Dashboard (mapped shape) and Reviews page (API shape) look the same
    r() {
      const rev = this.review
      if (!rev) return {}
      const replyText = rev.replyText || rev.reply_text || rev.reply || ''
      const repliedAtRaw = rev.repliedAt || rev.reply_updated || rev.reply_updated_at
      let repliedAt = null
      if (repliedAtRaw) {
        if (typeof repliedAtRaw === 'string') {
          // API returns reply_updated as "MM/DD/YYYY HH:MM:SS" – just show the date part
          repliedAt = repliedAtRaw.split(' ')[0]
        } else {
          repliedAt = this.formatDate(repliedAtRaw)
        }
      }
      const daysAgo = rev.days_ago || (rev.review_timestamp ? this.formatDaysAgo(rev.review_timestamp) : '')
      const shared = rev.shared || {
        facebook: rev.facebook_shared_at ? this.formatDate(rev.facebook_shared_at) : null,
        instagram: rev.instagram_shared_at ? this.formatDate(rev.instagram_shared_at) : null,
        google: rev.google_shared_at ? this.formatDate(rev.google_shared_at) : null,
      }
      return {
        ...rev,
        author_title: rev.author_title || 'Anonymous',
        author_image: rev.author_image,
        review_rating: rev.review_rating != null ? rev.review_rating : 5,
        review_text: rev.review_text || '',
        days_ago: daysAgo,
        replyText: replyText,
        reply: rev.reply ?? rev.reply_text ?? replyText,
        repliedAt,
        shared,
        platform: rev.platform || 'Google',
        facebook_post: rev.facebook_post,
        instagram_post: rev.instagram_post,
        gbp_post: rev.gbp_post,
      }
    },
    /** True when Google Profile has confirmed a reply (reply_updated / repliedAt). */
    isReplyPostedToGoogle() {
      return !!(this.r && this.r.repliedAt)
    },
    /** Local or AI-filled reply body exists but may not be posted to Google yet. */
    hasReplyDraft() {
      if (!this.r) return false
      const text = `${this.r.replyText || this.r.reply || ''}`.trim()
      return text.length > 0
    },
    hasActions() {
      return typeof this.createReply === 'function'
    },
  },
  methods: {
    startInlineEdit(review) {
      if (!review) return
      // Initialize reply with current text if empty
      if (!review.reply) {
        review.reply = this.r.replyText || ''
      }
      review.editing = true
    },
    getInitials(name) {
      if (!name) return ''
      return name
        .split(' ')
        .map((word) => word[0].toUpperCase())
        .join('')
        .slice(0, 2) // limits to 2 letters
    },
    formatDate(dateString) {
      if (!dateString) return null
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })
    },
    formatDaysAgo(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffDays = Math.floor(diffMs / 86400000)
      const diffMonths = Math.floor(diffDays / 30)
      const diffYears = Math.floor(diffMonths / 12)
      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return '1 day ago'
      if (diffDays < 30) return `${diffDays} days ago`
      if (diffMonths === 1) return '1 month ago'
      if (diffMonths < 12) return `${diffMonths} months ago`
      if (diffYears === 1) return '1 year ago'
      return `${diffYears} years ago`
    },
  },
}
</script>
